import React, {FC, useEffect, useLayoutEffect, useRef, useState} from "react";
import firstStep from "../../../../assets/images/icons/welcomeSteps/1.svg"
import secondStep from "../../../../assets/images/icons/welcomeSteps/2.svg"
import thirdStep from "../../../../assets/images/icons/welcomeSteps/3.svg"
import fourStep from "../../../../assets/images/icons/welcomeSteps/4.svg"
import fiveStep from "../../../../assets/images/icons/welcomeSteps/5.svg"
import sixStep from "../../../../assets/images/icons/welcomeSteps/6.svg"


type Placement = 'top' | 'left' | 'right' | 'bottom';

interface Step {
    target: string;
    title: string;
    content: string;
    placement?: Placement;
    borderRadius?: number;
    secondaryTarget?: string;
}

interface Rect {
    top: number;
    left: number;
    width: number;
    height: number;
}

const steps: Step[] = [
    {
        target: '.client-dashboard__attention',
        title: "Pending Tasks & Actions",
        content: 'This section highlights any tasks or action items that require your input. Be sure to check it regularly so you don\'t miss anything important and we can keep your projects moving efficiently!',
        placement: 'right',
        borderRadius: 10,
    },
    {
        target: '.header-credit-balance',
        title: "Credits",
        content: 'Use your credits for new legal tasks, calls, and documents. You can check your current balance and expiration dates right from your dashboard, just make sure to use your credits before they expire!',
        placement: 'left',
        secondaryTarget: '.credit-counter',
        borderRadius: 10,
    },
    {
        target: '.header-new-request',
        title: "Submit New Tasks",
        content: 'Submit a task to your VLO by clicking "Create a Task" whenever you need their assistance. Initiating any task costs 1 credit. Your VLO will inform you if additional credits are needed for completing the task.',
        placement: 'left',
        borderRadius: 25,
    },
    {
        target: '.nav-data-legal-projects',
        title: "Your Legal Project",
        content: 'We\'ve set up your legal project and added suggested tasks for next steps. Take a look here! When you\'re ready to proceed with any suggestion, just click "Get Started"!',
        placement: 'right',
        borderRadius: 4,
    },
    {
        target: '.nav-data-tasks',
        title: "Tasks",
        content: 'View all your active tasks here and chat directly with your VLO in the task chat. Your initial chat is already live – check it out!',
        placement: 'right',
        borderRadius: 4,
    },
    {
        target: '.nav-data-book-new-calls',
        title: "Expert Calls Hub",
        content: 'Explore the available calls you can book with our experts here. After your calls are completed, you’ll also find your follow-ups in this section. Take a look!',
        placement: 'right',
        borderRadius: 4,
    },
];


export const CustomTour: FC<{ close: () => void }> = ({ close }) => {
    const [stepIndex, setStepIndex] = useState(0);
    const [primaryRect, setPrimaryRect] = useState<Rect | null>(null);
    const [secondaryRect, setSecondaryRect] = useState<Rect | null>(null);
    const [tooltipDims, setTooltipDims] = useState({ width: 0, height: 0 });
    const [animating, setAnimating] = useState(false);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const step: Partial<Step> = steps[stepIndex] || {};
    const { placement = 'bottom', target, secondaryTarget, borderRadius = 4 } = step;

    useEffect(() => {
        const prev = document.body.style.overflowX;
        document.body.style.overflowX = 'hidden';
        return () => { document.body.style.overflowX = prev; };
    }, []);

    const updateRects = () => {
        if (target) {
            const mainEl = document.querySelector(target) as HTMLElement;
            if (mainEl) {
                const { top, left, width, height } = mainEl.getBoundingClientRect();
                setPrimaryRect({ top, left, width, height });
            }
        }
        if (secondaryTarget) {
            const secEl = document.querySelector(secondaryTarget) as HTMLElement;
            if (secEl) {
                const { top, left, width, height } = secEl.getBoundingClientRect();
                setSecondaryRect({ top, left, width, height });
            }
        } else {
            setSecondaryRect(null);
        }
        setAnimating(false);
        requestAnimationFrame(() => setAnimating(true));
    };

    useEffect(() => {
            const current = steps[stepIndex];
            if (current?.target) {
                const el = document.querySelector(current.target);
                if (!el) {
                    if (stepIndex < steps.length - 1) {
                        setStepIndex(stepIndex + 1);
                    } else {
                        close();
                    }
                }
            }
    }, [stepIndex, close]);

    useEffect(() => {
        updateRects();
        window.addEventListener('scroll', updateRects, { passive: true });
        window.addEventListener('resize', updateRects);
        return () => {
            window.removeEventListener('scroll', updateRects);
            window.removeEventListener('resize', updateRects);
        };
    }, [stepIndex, target, secondaryTarget]);

    useLayoutEffect(() => {
        if (tooltipRef.current) {
            const { width, height } = tooltipRef.current.getBoundingClientRect();
            setTooltipDims({ width, height });
        }
    }, [stepIndex, primaryRect]);

    const handleNext = () => stepIndex < steps.length - 1 && setStepIndex(stepIndex + 1);
    const handleBack = () => stepIndex > 0 && setStepIndex(stepIndex - 1);

    if (stepIndex === -1 || !primaryRect) return null;

    const offset = 10;
    let ttTop = 0, ttLeft = 0;
    const { width: ttW, height: ttH } = tooltipDims;
    switch (placement) {
        case 'top':
            ttTop = primaryRect.top - ttH - offset;
            ttLeft = primaryRect.left;
            break;
        case 'left':
            ttTop = primaryRect.top;
            ttLeft = primaryRect.left - ttW - offset;
            break;
        case 'right':
            ttTop = primaryRect.top;
            ttLeft = primaryRect.left + primaryRect.width + offset;
            break;
        default:
            ttTop = primaryRect.top + primaryRect.height + offset;
            ttLeft = primaryRect.left;
    }

    return (
        <>
            <svg className="tour-mask" data-animating={animating}>
                <defs>
                    <mask id="mask">
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        <rect
                            className="tour-highlight"
                            x={primaryRect.left}
                            y={primaryRect.top}
                            width={primaryRect.width}
                            height={primaryRect.height}
                            rx={borderRadius}
                        />
                        {secondaryRect && (
                            <rect
                                className="tour-highlight"
                                x={secondaryRect.left}
                                y={secondaryRect.top}
                                width={secondaryRect.width}
                                height={secondaryRect.height}
                                rx={borderRadius}
                            />
                        )}
                    </mask>
                </defs>
                <rect x="0" y="0" width="100%" height="100%" className="tour-overlay" mask="url(#mask)" />
            </svg>

            <div
                ref={tooltipRef}
                className="tour-tooltip"
                style={{ top: ttTop, left: ttLeft }}
            >
                <div className="tour-tooltip-arrow" data-placement={placement} />
                <div className="flex alignCenter justifySpaceBetween">
                    <h4 className="tour-tooltip__title">{step.title}</h4>
                    <div className="tour-tooltip__steps-info">
                        <div className="flex alignCenter justifySpaceBetween">
                            <h5>
                                <b>STEP</b>&nbsp;
                                <span>{stepIndex + 1}/{steps.length}</span>
                            </h5>
                            {stepIndex === 0 && <img className="ml5" src={firstStep} alt="" />}
                            {stepIndex === 1 && <img className="ml5" src={secondStep} alt="" />}
                            {stepIndex === 2 && <img className="ml5" src={thirdStep} alt="" />}
                            {stepIndex === 3 && <img className="ml5" src={fourStep} alt="" />}
                            {stepIndex === 4 && <img className="ml5" src={fiveStep} alt="" />}
                            {stepIndex === 5 && <img className="ml5" src={sixStep} alt="" />}
                        </div>
                    </div>
                </div>

                <p className="tour-tooltip-content">{step.content}</p>
                <div className="tour-buttons">
                    <button
                        className="tour-tooltip__btn-back"
                        onClick={handleBack}
                        disabled={stepIndex === 0}
                    >
                        GO BACK
                    </button>
                    {stepIndex < steps.length - 1 ? (
                        <button className="tour-tooltip__btn-next" onClick={handleNext}>NEXT</button>
                    ) : (
                        <button className="tour-tooltip__btn-next" onClick={close}>Finish</button>
                    )}
                </div>
            </div>
        </>
    );
};